/**
 * useAudio - Hook pour gérer les sons du jeu
 * Gère le preload, play/stop, et respecte l'état soundEnabled
 */

import { useEffect, useRef, useCallback } from 'react';
import { useGame } from '../context/GameContext';

// Types de sons disponibles
export type SoundType =
  | 'slot-spin'      // Musique pendant la roulette
  | 'slot-reveal'    // Jingle révélation slot
  | 'pacman-chase'   // Musique chasse aux fantômes
  | 'pacman-capture' // Son capture fantôme
  | 'reveal';        // Jingle révélation nom (commun)

// Mapping des fichiers audio
const SOUND_FILES: Record<SoundType, string> = {
  'slot-spin': '/sounds/slot-spin.wav',
  'slot-reveal': '/sounds/slot-reveal.wav',
  'pacman-chase': '/sounds/pacman-chase.mp3',
  'pacman-capture': '/sounds/pacman-capture.wav',
  'reveal': '/sounds/slot-reveal.wav', // Réutilise slot-reveal pour le moment
};

// Cache global des Audio elements (évite de recharger à chaque render)
const audioCache: Map<SoundType, HTMLAudioElement> = new Map();

// Preload un son
function preloadSound(type: SoundType): HTMLAudioElement {
  if (!audioCache.has(type)) {
    const audio = new Audio(SOUND_FILES[type]);
    audio.preload = 'auto';
    audioCache.set(type, audio);
  }
  return audioCache.get(type)!;
}

// Preload tous les sons au chargement
export function preloadAllSounds(): void {
  Object.keys(SOUND_FILES).forEach((type) => {
    preloadSound(type as SoundType);
  });
}

interface UseAudioReturn {
  play: (type: SoundType, options?: { loop?: boolean; volume?: number }) => void;
  stop: (type: SoundType) => void;
  stopAll: () => void;
  isEnabled: boolean;
}

export function useAudio(): UseAudioReturn {
  const { state } = useGame();
  const currentlyPlaying = useRef<Set<SoundType>>(new Set());

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      // Stop tous les sons quand le composant est démonté
      currentlyPlaying.current.forEach((type) => {
        const audio = audioCache.get(type);
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
      currentlyPlaying.current.clear();
    };
  }, []);

  const play = useCallback((type: SoundType, options?: { loop?: boolean; volume?: number }) => {
    if (!state.soundEnabled) return;

    const audio = preloadSound(type);
    audio.loop = options?.loop ?? false;
    audio.volume = options?.volume ?? 0.5;
    audio.currentTime = 0;

    audio.play().catch((err) => {
      // Ignore les erreurs de autoplay policy
      console.debug('Audio play prevented:', err.message);
    });

    currentlyPlaying.current.add(type);

    // Retirer du set quand le son se termine (si pas en loop)
    if (!options?.loop) {
      audio.onended = () => {
        currentlyPlaying.current.delete(type);
      };
    }
  }, [state.soundEnabled]);

  const stop = useCallback((type: SoundType) => {
    const audio = audioCache.get(type);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      currentlyPlaying.current.delete(type);
    }
  }, []);

  const stopAll = useCallback(() => {
    audioCache.forEach((audio, type) => {
      audio.pause();
      audio.currentTime = 0;
      currentlyPlaying.current.delete(type);
    });
  }, []);

  return {
    play,
    stop,
    stopAll,
    isEnabled: state.soundEnabled,
  };
}
